import React, { useEffect, useState } from "react";
import { triggerInstallPrompt, isStandaloneMode, isIos } from "@/lib/pwa";
import { X, Download, Share as ShareIcon } from "lucide-react";

const DISMISS_KEY = "summit_air_install_hint_dismissed";

/**
 * Small dismissible install banner. Shown when:
 *  - app is NOT already installed / running in standalone mode,
 *  - the user hasn't dismissed it in the last 14 days,
 *  - either the browser fired beforeinstallprompt (Android/Chrome), or the
 *    device is iOS (in which case we show manual instructions).
 */
export default function InstallHint() {
  const [visible, setVisible] = useState(false);
  const [installable, setInstallable] = useState(false);

  useEffect(() => {
    if (isStandaloneMode()) return;
    // Respect a fresh dismissal — hide for 14 days.
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < 14 * 24 * 60 * 60 * 1000) return;

    const onInstallable = () => {
      setInstallable(true);
      setVisible(true);
    };
    window.addEventListener("summit-air:installable", onInstallable);
    // On iOS, no beforeinstallprompt — surface the hint anyway with manual steps.
    if (isIos()) setVisible(true);
    return () => window.removeEventListener("summit-air:installable", onInstallable);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };
  const install = async () => {
    const outcome = await triggerInstallPrompt();
    if (outcome === "accepted") setVisible(false);
    if (outcome === "unavailable") {
      // Fall through — banner stays visible so iOS users see the instructions.
    }
  };

  if (!visible) return null;

  return (
    <div
      className="mb-4 rounded-lg border border-summit-primary/20 bg-summit-primary text-white px-4 py-3 flex items-start gap-3 shadow-sm"
      data-testid="install-hint"
    >
      <div className="w-8 h-8 shrink-0 rounded-md bg-white/10 flex items-center justify-center">
        <Download className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-sm font-medium tracking-tight">
          Install SA on your phone
        </div>
        {isIos() ? (
          <p className="mt-0.5 text-xs text-white/80 leading-relaxed" data-testid="install-hint-ios">
            Tap the <ShareIcon className="inline w-3 h-3 -mt-0.5" /> Share button, then{" "}
            <b>Add to Home Screen</b>. You&apos;ll get one-tap clock-in and offline access.
          </p>
        ) : installable ? (
          <p className="mt-0.5 text-xs text-white/80">
            One-tap clock-in, offline timelogs, faster launch than the browser.
          </p>
        ) : (
          <p className="mt-0.5 text-xs text-white/80">
            Add SA to your Home screen for one-tap clock-in and offline access.
          </p>
        )}
        {installable && !isIos() && (
          <button
            type="button"
            onClick={install}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-white text-summit-primary px-3 py-1.5 text-xs font-mono uppercase tracking-widest hover:bg-summit-glacier"
            data-testid="install-hint-install-btn"
          >
            <Download className="w-3.5 h-3.5" /> Install
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 p-1 -m-1 rounded-md hover:bg-white/10"
        aria-label="Dismiss"
        data-testid="install-hint-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
